/**
 * Presentation / TV power-up surfaces (#232, v1) — public info only.
 *
 *  - {@link PowerUpThreatStrip}: a persistent "Cards in hand" strip under the board
 *    (mocks 07/12) showing each player's token counts + a transient ✦ glow on the
 *    player who just gained a card. It shows the *capability*, never "because behind".
 *  - {@link PowerUpPlayedReveal}: an attributed "PLAYER played CARD" slam (mock 06/08)
 *    fired from the broadcast `powerup_played` event. Carries no secret.
 *
 * The Sneak Peek *hint* is private and is NEVER surfaced here — only the fact that a
 * player played the Sneak Peek card (public juice) may appear.
 */

import type { CSSProperties } from 'react';
import { POWERUP_KINDS } from '@jeopardy/shared';
import type { PowerUpKind, Player } from '@jeopardy/shared';
import { POWERUP_CARD_META } from './PowerUpHand';

/** TV-styled money (commas, negative aware) — matches the mockups' `$3,600`. */
function money(score: number): string {
  const abs = Math.abs(score).toLocaleString('en-US');
  return score < 0 ? `-$${abs}` : `$${abs}`;
}

export interface PowerUpThreatStripProps {
  players: Player[];
  /** The player who just gained a card — glows ✦ briefly (from `powerup_granted`). */
  newCardPlayerId?: string | null;
}

/** The "Cards in hand" strip that sits below the board (mocks 07/12). */
export function PowerUpThreatStrip({
  players,
  newCardPlayerId,
}: PowerUpThreatStripProps): React.JSX.Element {
  return (
    <div className="tv-strip" data-testid="tv-threat-strip">
      <p className="tv-strip-title">Cards in hand</p>
      <div className="tv-threat">
        {players.map((p) => {
          const tokens = p.tokens ?? {};
          const isNew = newCardPlayerId != null && p.id === newCardPlayerId;
          // Expand counts → one mini-card per held token (mock shows a card per token).
          const held: PowerUpKind[] = [];
          for (const kind of POWERUP_KINDS) {
            for (let i = 0; i < (tokens[kind] ?? 0); i += 1) held.push(kind);
          }
          return (
            <div
              key={p.id}
              className={'tv-holder' + (isNew ? ' tv-holder--new' : '')}
              data-testid={`tv-holder-${p.id}`}
            >
              <div className="tv-holder__name">{p.name}</div>
              <div className="tv-holder__score">{money(p.score)}</div>
              <div className="tv-holder__cards">
                {held.length === 0 ? (
                  <span className="tv-holder__empty">—</span>
                ) : (
                  held.map((kind, i) => (
                    <span
                      key={i}
                      className={`mini-card mini-card--${POWERUP_CARD_META[kind].slug}`}
                      aria-label={POWERUP_CARD_META[kind].name}
                    >
                      {POWERUP_CARD_META[kind].icon}
                    </span>
                  ))
                )}
              </div>
              {isNew && <div className="tv-holder__flag">✦ new card</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Short public effect line for a played card (no secret). Snake Bite is STEALTH — its
 * play is never broadcast, so it never reaches the TV; the entry is a safe fallback only.
 */
const PLAYED_EFFECT: Record<PowerUpKind, string> = {
  DOUBLE_UP: 'Going for 2× on this clue',
  SNEAK_PEEK: 'Took a private peek',
  STEAL: 'Stole the rebound!',
  BRAIN_FREEZE: 'Chilled the whole room',
  SNAKE_BITE: 'Made a quiet move',
  INSURANCE: 'Playing it safe — loss halved',
  WAGER_BOOST: 'Rolled the die for a bigger wager',
  TAG_TEAM: 'Called in a teammate',
};

export interface PowerUpPlayedRevealProps {
  playerName: string;
  powerup: PowerUpKind;
}

/** The attributed card-played slam (mock 06/08 styling). */
export function PowerUpPlayedReveal({
  playerName,
  powerup,
}: PowerUpPlayedRevealProps): React.JSX.Element {
  const meta = POWERUP_CARD_META[powerup];
  const accent = { '--accent': `var(--c-${meta.slug})` } as CSSProperties;
  return (
    <div className="tv-play" data-testid="tv-powerup-played" style={accent}>
      <div className="tv-play__slam" style={accent}>
        <div className="tv-play__icon" aria-hidden="true">
          {meta.icon}
        </div>
        <div>
          <div className="tv-play__who">
            <b>{playerName.toUpperCase()}</b> played {meta.name.toUpperCase()}
          </div>
          <div className="tv-play__what">{PLAYED_EFFECT[powerup]}</div>
        </div>
      </div>
    </div>
  );
}
